import React, { useEffect, useRef, useState } from 'react';
import { Button, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { routesPath } from '../../constants/routesPath';
import { useExchangeInternalSsoMutation } from '../../redux/apiService/auth';
import { getSafeInternalReturnPath } from '../../services/internalSsoService';
import storageService from '../../services/storageService';

export const InternalSsoContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fragmentParams = new URLSearchParams(location.hash.replace(/^#/, ''));
  const codeRef = useRef(
    fragmentParams.get('code') || new URLSearchParams(location.search).get('code'),
  );
  const exchangeStarted = useRef(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [exchangeInternalSso, exchangeState] = useExchangeInternalSsoMutation();

  const exchangeCode = async () => {
    const code = codeRef.current;
    if (!/^[a-f0-9]{64}$/i.test(code || '')) {
      setErrorMessage('This sign-in link is invalid or incomplete.');
      return;
    }

    setErrorMessage('');
    try {
      const response = await exchangeInternalSso({ code }).unwrap();
      storageService.setAuthenticatedSession(response.data);
      codeRef.current = null;
      toast.success('Successfully signed in!');
      navigate(getSafeInternalReturnPath(response.data.return_path), { replace: true });
    } catch (error) {
      const responseData = error?.data;
      const serverMessage = Array.isArray(responseData?.errors)
        ? responseData.errors[0]
        : responseData?.errors || responseData?.messages;
      setErrorMessage(serverMessage || 'The sign-in link expired or could not be used.');
    }
  };

  useEffect(() => {
    // Remove the temporary credential before exchanging it so refreshes,
    // copied URLs, and browser history do not retain it.
    window.history.replaceState({}, document.title, routesPath.internalSso);
    if (!exchangeStarted.current) {
      exchangeStarted.current = true;
      exchangeCode();
    }
    // The code is captured once in memory and must never be added to dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="py-5 text-center">
      {!errorMessage ? (
        <>
          <h2>Signing you in...</h2>
          <p>Please keep this page open while your Holedo session is connected to TMR.</p>
        </>
      ) : (
        <>
          <h2>We could not sign you in</h2>
          <p>{errorMessage}</p>
          <Button color="primary" onClick={exchangeCode} disabled={exchangeState.isLoading || !codeRef.current}>
            Try again
          </Button>{' '}
          <Button color="secondary" onClick={() => navigate(routesPath.login, { replace: true })}>
            Go to login
          </Button>
        </>
      )}
    </Container>
  );
};
